import React, { useState } from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Briefcase,
  Image,
  MessageSquare,
  HelpCircle,
  Settings,
  Mail,
  LogOut,
  Menu,
  X,
  ExternalLink,
  Camera,
  FileText,
  Ticket,
  Tags,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';

const navItems = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/quote-requests', icon: Mail, label: 'Quote Requests' },
  { to: '/admin/events', icon: Camera, label: 'Events' },
  { to: '/admin/categories', icon: Tags, label: 'Categories' },
  { to: '/admin/page-content', icon: FileText, label: 'Page Content' },
  { to: '/admin/packages', icon: Package, label: 'Packages' },
  { to: '/admin/promo-codes', icon: Ticket, label: 'Promotions' },
  { to: '/admin/services', icon: Briefcase, label: 'Services' },
  { to: '/admin/gallery', icon: Image, label: 'Gallery' },
  { to: '/admin/testimonials', icon: MessageSquare, label: 'Testimonials' },
  { to: '/admin/faqs', icon: HelpCircle, label: 'FAQs' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#1A1612] text-[#EADBC8]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-[#1F1A17] border-b border-[#C5A880]/20 px-4 h-16 flex items-center justify-between">
        <div className="text-xl font-bold">
          <span className="text-[#EADBC8]">Mayden</span>
          <span className="text-[#C5A880] ml-1">Films</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="text-[#EADBC8]"
          data-testid="admin-mobile-menu-btn"
        >
          {sidebarOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-[#1F1A17] border-r border-[#C5A880]/20 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          <div className="hidden lg:block px-6 py-6 border-b border-[#C5A880]/20">
            <div className="text-xl font-bold">
              <span className="text-[#EADBC8]">Mayden</span>
              <span className="text-[#C5A880] ml-1">Films</span>
            </div>
            <p className="text-gray-400 text-xs mt-1">Admin Panel</p>
          </div>

          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto mt-16 lg:mt-0">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-[#C5A880] text-[#1F1A17]'
                        : 'text-gray-400 hover:bg-[#2A2420] hover:text-[#EADBC8]'
                    }`
                  }
                >
                  <Icon size={18} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="px-4 py-4 border-t border-[#C5A880]/20 space-y-2">
            <div className="flex items-center gap-3 px-4 py-2">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#C5A880] flex items-center justify-center text-[#1F1A17] font-bold text-sm">
                  {user?.name?.[0] || 'A'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[#EADBC8] text-sm truncate">{user?.name}</p>
                <p className="text-gray-500 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              onClick={() => window.open('/', '_blank')}
              variant="outline"
              className="w-full border-[#C5A880]/20 text-gray-300 hover:bg-[#2A2420]"
              data-testid="admin-view-site-btn"
            >
              <ExternalLink size={16} className="mr-2" />
              View Site
            </Button>
            <Button
              onClick={logout}
              variant="outline"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid="admin-logout-btn"
            >
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-20 bg-[#1F1A17]/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-6 lg:p-10 max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
